import { Trans } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { CenteredShell, Eyebrow, Headline } from '../components/auth-shells.js';
import { Mag } from '../components/mag.js';

const searchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute('/magic-link/sent')({
  validateSearch: searchSchema,
  component: MagicLinkSentPage,
});

// Handoff §15.2.3 — centered. Casting Mag + serif italic + tilted inbox-
// preview card. Includes resend / use password instead fallbacks.
function MagicLinkSentPage() {
  const { email } = Route.useSearch();
  return (
    <CenteredShell>
      <Mag size={120} />
      <Eyebrow>
        <Trans>check your inbox</Trans>
      </Eyebrow>
      <Headline>
        <Trans>mag sent you a link.</Trans>
      </Headline>
      <p className="font-serif text-xl leading-tight text-[var(--ink)]/80">
        <Trans>“tap the button in the email — i'll open the door.”</Trans>
      </p>
      {email ? (
        <p className="text-[14px] text-[var(--ink)]/70">
          <Trans>
            sent to <span className="font-mono">{email}</span>
          </Trans>
        </p>
      ) : null}

      {/* Tilted inbox-preview card so the user knows what to look for. */}
      <div
        className="mt-2 w-full max-w-[360px] rounded-xl border border-[var(--ink)] bg-white p-4 text-left shadow-[4px_4px_0_var(--plum)]"
        style={{ transform: 'rotate(-2deg)' }}
      >
        <div className="flex items-center gap-2">
          <div className="grid h-7 w-7 place-items-center rounded-full bg-[var(--ink)] text-[var(--spell)]">
            ✦
          </div>
          <div>
            <p className="text-[13px] font-semibold">Mag</p>
            <p className="text-[11px] font-mono uppercase tracking-[0.12em] text-[var(--ink)]/60">
              <Trans>tap to sign in.</Trans>
            </p>
          </div>
        </div>
        <p className="mt-2 text-[12px] text-[var(--ink)]/70">
          <Trans>link expires in 5 minutes — request another if it times out.</Trans>
        </p>
      </div>

      <div className="mt-4 flex flex-col items-center gap-2">
        <Link to="/login" className="link-plum">
          <Trans>send it again</Trans>
        </Link>
        <Link to="/login" className="link-quiet">
          <Trans>use password instead</Trans>
        </Link>
      </div>
    </CenteredShell>
  );
}
